import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Divider,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { backendBaseUrl } from '../app/config';
import { listModels } from './model/modelApi';

/**
 * The typed confirmation of the ONE purge-everything action. `WorkspaceEndpoints.PurgeEverythingConfirmation`
 * compares the request's `confirm` field with `StringComparison.Ordinal`, so this literal is a byte-for-byte
 * protocol value and is NEVER translated: it lives here, not in `locales/<lang>/common.json`. What IS
 * translated is the instruction around it, which interpolates this value in untranslated.
 */
export const PURGE_EVERYTHING_CONFIRMATION = 'PURGE_EVERYTHING';

// A dropped stream is not a failed purge: the server finishes the purge without the client. The page
// then re-reads the persisted project and model lists (persist-then-render) every CONNECTION_RECHECK_MS, at
// most CONNECTION_RECHECK_ATTEMPTS times, and only reports done when neither a project nor a model remains.
// The attempts cover the server's 10-minute drain budget (240 x 2.5s). Deliberately NOT 3000ms — that value
// is the success redirect's timer.
export const CONNECTION_RECHECK_MS = 2500;
export const CONNECTION_RECHECK_ATTEMPTS = 240;

export function ClearAllPage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [status, setStatus] = React.useState<'idle' | 'clearing' | 'verifying' | 'success' | 'error'>('idle');
  const [confirmText, setConfirmText] = React.useState('');
  const [result, setResult] = React.useState<Record<string, number> | null>(null);
  // The persisted state READ BACK after the action (persist-then-render): never inferred from the stream.
  const [readBack, setReadBack] = React.useState<{ projects: number; models: number } | null>(null);
  const [readBackFailed, setReadBackFailed] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [progress, setProgress] = React.useState(0);
  const [progressLabel, setProgressLabel] = React.useState('');

  const apiBase = backendBaseUrl;

  // EXACT and deliberately NOT trimmed: the server compares Ordinal, so trimming here would enable the
  // button on input the server refuses.
  const confirmed = confirmText === PURGE_EVERYTHING_CONFIRMATION;

  // The two persisted reads that decide whether the platform is actually empty. Throws on any failure.
  const readPersistedState = async (): Promise<{ projects: number; models: number }> => {
    const res = await fetch(`${apiBase}/api/workspaces`);
    if (!res.ok) throw new Error(`workspace list ${res.status}`);
    const body = await res.json();
    if (!Array.isArray(body?.workspaces)) throw new Error('workspace list has no workspaces array');
    const models = await listModels();
    return { projects: body.workspaces.length, models: models.count };
  };

  const handleClearAll = async () => {
    if (!confirmed) return;

    setStatus('clearing');
    setError(null);
    setProgress(0);
    setReadBack(null);
    setReadBackFailed(false);
    setProgressLabel(t('common:clearAll.connecting'));

    // 🔴 WI-51 — THE TERMINAL VERDICT IS DERIVED FROM LOCALS, NOT FROM REACT STATE, AND THAT IS
    // THE FIX. The previous shape read `status` after the stream loop (`if (status !== 'success')`)
    // to decide whether the stream had ended without a `complete` event. `status` is captured by
    // this closure at render time, and the button that invokes this handler renders ONLY inside
    // `{status === 'idle' && (…)}` — so the running closure ALWAYS held `'idle'`, the guard could
    // never be false, and its body unconditionally overwrote the `setStatus('error')` the
    // `global_incomplete` arm had just performed. The `global_incomplete` arm was dead code and the
    // comment above it ("Never render the unconditional success screen over a partial failure") was
    // a guard whose name was a false claim. Locals are written and read by the SAME invocation, so
    // there is no stale-capture hazard and exactly ONE place decides the terminal.
    //
    // ⚠️ A STREAM THAT ENDS WITHOUT `complete` IS A FAILURE, NOT A SUCCESS. The backend writes its
    // `CLEAR_ALL_EXECUTED` audit row BEFORE it emits `complete` (WorkspaceEndpoints
    // RecordDestructiveDeletionAsync, "ORDER IS LOAD-BEARING"), precisely so a torn stream cannot
    // be reported as a clean wipe. Absence of `complete` therefore means the purge's own record of
    // itself never landed — `frontend-rules.md`: never show success when the backend did not.
    let completeSeen = false;
    let streamOpened = false;
    let globalIncomplete = false;
    let remainingProjects = 0;

    try {
      const response = await fetch(`${apiBase}/api/workspaces/clear-all`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        // The typed value, not the constant: the server is the sole judge of the comparison.
        body: JSON.stringify({ confirm: confirmText }),
      });

      if (!response.ok) {
        // 403 and 400 are decided BEFORE the stream opens, so nothing was deleted and the page says so.
        if (response.status === 403) throw new Error(t('common:clearAll.refusedNotAdmin'));
        if (response.status === 400) throw new Error(t('common:clearAll.refusedConfirmation'));
        throw new Error(t('common:clearAll.failedToClear', { status: response.status, statusText: response.statusText }));
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error(t('common:clearAll.noResponseStream'));

      streamOpened = true;
      const decoder = new TextDecoder();
      let buffer = '';

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          streamDone = true;
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ') && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              if (eventType === 'start') {
                setProgressLabel(t('common:clearAll.purgingWorkspaces', { count: data.total_workspaces }));
              } else if (eventType === 'drain_progress') {
                setProgressLabel(t('common:clearAll.removingProjects', { count: data.remaining_workspaces }));
              } else if (eventType === 'heartbeat') {
                // Liveness only (keeps the proxy's idle wall from closing the stream); no payload to render.
              } else if (eventType === 'progress') {
                setProgressLabel(t('common:clearAll.removingData'));
              } else if (eventType === 'complete') {
                setResult(data.deleted_counts);
                completeSeen = true;
                remainingProjects = Number(data.remaining_workspaces ?? 0);
                // Error-honesty (frontend-rules): the BE stamps global_incomplete when any
                // global-phase store failed to wipe (per-store accounting in DeletionResult).
                // The failed-store NAMES are internal topology (TD-321) — console/log only,
                // never UI. The terminal itself is decided once, after the loop.
                globalIncomplete = Boolean(data.global_incomplete);
                if (globalIncomplete) {
                  console.error('[ClearAllPage] global wipe incomplete; failed stores:', data.global_failed_stores);
                }
              } else {
                // Whitelist-drop guard: an event the backend emits and this chain does not know
                // about is surfaced, not silently discarded — that drop is exactly how
                // two per-workspace failure events went unhandled for the life of this page.
                console.warn('[ClearAllPage] unhandled clear-all event type:', eventType);
              }
            } catch (parseErr) {
              // Never a silent swallow (core-principles "fail loud"): a dropped frame in a
              // data-deletion stream is how a `complete` event goes missing, and a missing
              // `complete` now lands in the error terminal below rather than a fake success.
              console.error('[ClearAllPage] malformed clear-all SSE frame:', eventType, parseErr);
            }
            eventType = '';
          }
        }
      }

      // THE SINGLE TERMINAL DECISION. Success is the residual case — it is reached only when the
      // backend reported `complete` and reported the global phase whole. Since 2026-09-28 each
      // workspace is tombstoned and purged in the background (TD-1133), so a workspace that could
      // not be marked arrives as a global failure rather than a per-workspace event. Every other observation produces the error terminal, carrying
      // COUNTS only (TD-321: no store, workspace or service names in user-visible output).
      const failureReasons: string[] = [];
      if (!completeSeen) failureReasons.push(t('common:clearAll.streamEndedEarly'));
      if (globalIncomplete) {
        failureReasons.push(remainingProjects > 0
          ? t('common:clearAll.partialFailureRemaining', { count: remainingProjects })
          : t('common:clearAll.partialFailure'));
      }

      // PERSIST-THEN-RENDER: the verdict is confirmed against the server's own state. A stream that said
      // `complete` over a platform that still lists a project or a model is not a success. A read-back that
      // cannot be made is reported as such, never as a verified-empty platform.
      // Read on EVERY terminal, including a stream that ended with no `complete`: that is an outcome-unknown
      // state, and the operator needs the counts the server actually holds to decide on a re-run.
      try {
        const state = await readPersistedState();
        setReadBack(state);
        if (completeSeen && !globalIncomplete && (state.projects > 0 || state.models > 0)) {
          failureReasons.push(t('common:clearAll.readBackNotEmpty', { projects: state.projects, models: state.models }));
        }
      } catch (readBackErr) {
        console.error('[ClearAllPage] re-reading the platform after the purge failed:', readBackErr);
        setReadBackFailed(true);
      }

      if (failureReasons.length > 0) {
        setError(failureReasons.join(' '));
        setStatus('error');
        return;
      }

      setStatus('success');
      setTimeout(() => { window.location.href = '/'; }, 3000);
    } catch (err) {
      if (streamOpened) {
        // The request was accepted and the stream then broke (network drop, proxy idle timeout). The
        // purge survives a client disconnect, so ask the server what is left instead of showing the
        // browser's raw transport text.
        console.warn('[ClearAllPage] purge stream dropped; verifying against the persisted state:', err);
        await verifyAfterDrop();
        return;
      }
      setError(err instanceof Error ? err.message : t('common:clearAll.unknownError'));
      setStatus('error');
    }
  };

  const verifyAfterDrop = async () => {
    setStatus('verifying');
    setProgressLabel(t('common:clearAll.connectionLost'));
    for (let attempt = 0; attempt < CONNECTION_RECHECK_ATTEMPTS; attempt++) {
      try {
        const state = await readPersistedState();
        setReadBack(state);
        if (state.projects === 0 && state.models === 0) {
          setResult(null);
          setStatus('success');
          setTimeout(() => { window.location.href = '/'; }, 3000);
          return;
        }
      } catch (recheckErr) {
        console.warn('[ClearAllPage] platform recheck failed; will retry:', recheckErr);
      }
      await new Promise<void>((resolve) => setTimeout(resolve, CONNECTION_RECHECK_MS));
    }
    setError(t('common:clearAll.connectionLostUnconfirmed'));
    setStatus('error');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Light keeps the dark focus backdrop; the dark ramp inverts neutral[900] to near-white, so dark uses the page surface.
      bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.custom.colors.neutral[900],
      px: 3,
      py: { xs: 4, md: 6 },
      overflowY: 'auto',
    }}>
      <Paper elevation={6} sx={{
        p: 5,
        maxWidth: 550,
        width: '100%',
        borderRadius: theme.custom.borders.radius.xl,
        textAlign: 'center',
        bgcolor: theme.custom.colors.background.paper,
        maxHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 96px)' },
        overflowY: 'auto',
      }}>
        {status === 'idle' && (
          <>
            <Box sx={{ width: 80, height: 80, borderRadius: theme.custom.borders.radius.full, bgcolor: theme.custom.colors.warning.light, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <WarningAmberIcon sx={{ fontSize: 48, color: theme.custom.colors.warning.main }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: theme.custom.typography.fontWeight.bold, mb: 1, color: theme.custom.colors.text.primary }}>
              {t('common:clearAll.idleTitle')}
            </Typography>

            <Typography variant="body1" sx={{ color: theme.custom.colors.text.tertiary, mb: 3, lineHeight: 1.7 }}>
              <Trans i18nKey="common:clearAll.idleDescription" components={{ strong: <strong /> }} />
            </Typography>

            <Alert severity="error" icon={<DeleteForeverIcon />} sx={{ mb: 3, textAlign: 'left', bgcolor: theme.custom.colors.error.light }}>
              <Typography variant="subtitle2" sx={{ fontWeight: theme.custom.typography.fontWeight.bold, mb: 0.5 }}>
                {t('common:clearAll.alertHeading')}
              </Typography>
              <Typography variant="body2">
                {t('common:clearAll.alertBody')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                <li>{t('common:clearAll.bulletWorkspaces')}</li>
                <li>{t('common:clearAll.bulletFiles')}</li>
                <li>{t('common:clearAll.bulletReports')}</li>
                <li>{t('common:clearAll.bulletLogs')}</li>
                <li>{t('common:clearAll.bulletModels')}</li>
              </Box>
            </Alert>

            <Typography variant="body2" sx={{ color: theme.custom.colors.text.tertiary, mb: 3, textAlign: 'left' }}>
              {t('common:clearAll.keptNote')}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" sx={{ fontWeight: theme.custom.typography.fontWeight.semibold, color: theme.custom.colors.text.secondary, mb: 1.5, textAlign: 'left' }}>
              {t('common:clearAll.confirmPrompt', { phrase: PURGE_EVERYTHING_CONFIRMATION })}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={PURGE_EVERYTHING_CONFIRMATION}
              inputProps={{ 'aria-label': t('common:clearAll.confirmLabel'), autoComplete: 'off', spellCheck: false }}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="error"
                size="large"
                disabled={!confirmed}
                startIcon={<DeleteForeverIcon />}
                onClick={handleClearAll}
                sx={{ px: 4, py: 1.5, fontWeight: theme.custom.typography.fontWeight.bold, bgcolor: confirmed ? theme.custom.colors.error.main : theme.custom.colors.neutral[200], '&.Mui-disabled': { bgcolor: theme.palette.mode === 'dark' ? theme.custom.colors.accent.surfaceHi : theme.custom.colors.neutral[200], color: theme.palette.mode === 'dark' ? theme.custom.colors.text.secondary : theme.custom.colors.text.disabled } }}
              >
                {t('common:clearAll.goButton')}
              </Button>
            </Box>
          </>
        )}

        {(status === 'clearing' || status === 'verifying') && (
          <>
            <Box sx={{ width: 80, height: 80, borderRadius: theme.custom.borders.radius.full, bgcolor: theme.custom.colors.error.light, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <DeleteForeverIcon sx={{ fontSize: 48, color: theme.custom.colors.error.main }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: theme.custom.typography.fontWeight.bold, color: theme.custom.colors.text.primary, mb: 1 }}>{t('common:clearAll.purgingTitle')}</Typography>
            <Typography variant="body2" sx={{ color: theme.custom.colors.text.tertiary, mb: 3 }}>{progressLabel}</Typography>
            {status === 'verifying' && readBack && (
              <Typography variant="body2" sx={{ color: theme.custom.colors.text.tertiary, mb: 2 }}>
                {t('common:clearAll.readBackNow', { projects: readBack.projects, models: readBack.models })}
              </Typography>
            )}
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress
                variant={status === 'verifying' ? 'indeterminate' : 'determinate'}
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: theme.custom.colors.neutral[100],
                  '& .MuiLinearProgress-bar': { bgcolor: theme.custom.colors.error.main, borderRadius: 4 },
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? theme.custom.colors.text.tertiary : theme.custom.colors.text.disabled, fontFamily: theme.custom.typography.fontFamily.mono }}>{status === 'verifying' ? '' : `${progress}%`}</Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Box sx={{ width: 80, height: 80, borderRadius: theme.custom.borders.radius.full, bgcolor: theme.custom.colors.success.light, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: theme.custom.colors.success.main }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: theme.custom.typography.fontWeight.bold, mb: 1, color: theme.custom.colors.success.main }}>{t('common:clearAll.successTitle')}</Typography>
            <Typography variant="body1" sx={{ color: theme.custom.colors.text.tertiary, mb: 3 }}>
              {t('common:clearAll.successBody')}
            </Typography>
            {readBack && (
              <Typography variant="body2" sx={{ color: theme.custom.colors.text.tertiary, mb: 2 }}>
                {t('common:clearAll.readBackNow', { projects: readBack.projects, models: readBack.models })}
              </Typography>
            )}
            {readBackFailed && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>{t('common:clearAll.readBackFailed')}</Alert>
            )}
            {result && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, textAlign: 'left', bgcolor: theme.custom.colors.neutral[50], borderRadius: theme.custom.borders.radius.lg }}>
                <Typography variant="subtitle2" sx={{ fontWeight: theme.custom.typography.fontWeight.bold, mb: 1, color: theme.custom.colors.text.secondary }}>{t('common:clearAll.deletedItemsHeading')}</Typography>
                <Divider sx={{ mb: 1 }} />
                {Object.entries(result).map(([key, count]) => (
                  <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" sx={{ color: theme.custom.colors.text.tertiary }}>{key.replace(/_/g, ' ')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: theme.custom.typography.fontWeight.semibold, fontFamily: theme.custom.typography.fontFamily.mono }}>{count}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
            <Button variant="contained" onClick={() => window.location.href = '/'} sx={{ px: 5, py: 1.5, fontWeight: theme.custom.typography.fontWeight.bold, bgcolor: theme.custom.colors.neutral[900] }}>
              {t('common:clearAll.goToWorkspaces')}
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Box sx={{ width: 80, height: 80, borderRadius: theme.custom.borders.radius.full, bgcolor: theme.custom.colors.error.light, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
              <WarningAmberIcon sx={{ fontSize: 48, color: theme.custom.colors.error.main }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: theme.custom.typography.fontWeight.bold, mb: 1, color: theme.custom.colors.error.main }}>{t('common:clearAll.errorTitle')}</Typography>
            <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>
            {readBack && (
              <Typography variant="body2" sx={{ color: theme.custom.colors.text.tertiary, mb: 2 }}>
                {t('common:clearAll.readBackNow', { projects: readBack.projects, models: readBack.models })}
              </Typography>
            )}
            {readBackFailed && !readBack && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>{t('common:clearAll.readBackUnavailable')}</Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => { setStatus('idle'); setConfirmText(''); }}>{t('common:clearAll.tryAgain')}</Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
